// Iter 114 — Frontend guard for the PLAYER's OUTBOUND payout wallet.
//
// Two responsibilities:
//   1. COIN_NETWORKS — the ONLY coin↔network combos VG will pay out on.
//      Must match the 9 configured payout rails (BTC / ETH / LTC / SOL /
//      TRX / XRP / USDC-ERC20 / USDT-ERC20 / USDT-TRC20). BEP-20 and
//      USDC on TRC20 or Solana are intentionally excluded — VG will not
//      pay a rebate on those rails.
//   2. validateAddressFormat — a prefix/charset sanity check so the
//      player cannot submit a Litecoin address after picking USDC and
//      lose their rebate to the wrong chain. This is only a guard —
//      server-side `validate_wallet` in backend/server.py stays the
//      final authority (full checksum + EIP-55 + base58check + bech32).
//
// NEVER collect a private key. Address is the PUBLIC receiving address.

export const COIN_NETWORKS = {
  BTC:  ["Bitcoin"],
  ETH:  ["Ethereum"],
  LTC:  ["Litecoin"],
  SOL:  ["Solana"],
  TRX:  ["Tron"],
  XRP:  ["XRPL"],
  USDC: ["ERC20"],           // ERC-20 only — TRC20/BEP20/SOL not paid out.
  USDT: ["ERC20", "TRC20"],  // ERC-20 or TRC-20 — BEP20/SOL not paid out.
};

export const COIN_OPTIONS = Object.keys(COIN_NETWORKS);

// Prefix/charset sanity — mirrors the shape backend/server.py::validate_wallet
// requires. Not a full checksum verifier; the server does that.
const RE_EVM         = /^0x[0-9a-fA-F]{40}$/;
const RE_BTC_LEGACY  = /^[13][a-km-zA-HJ-NP-Z1-9]{25,39}$/;   // P2PKH / P2SH
const RE_BTC_BECH32  = /^bc1[023456789ac-hj-np-z]{6,87}$/i;   // segwit
const RE_LTC_LEGACY  = /^[LM3][a-km-zA-HJ-NP-Z1-9]{25,39}$/;
const RE_LTC_BECH32  = /^ltc1[023456789ac-hj-np-z]{6,87}$/i;
const RE_TRON        = /^T[a-km-zA-HJ-NP-Z1-9]{33}$/;
const RE_SOL         = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;      // base58, no 0/O/I/l
const RE_XRP         = /^r[a-km-zA-HJ-NP-Z1-9]{24,34}$/;

/**
 * Returns null if the address looks valid for (coin, network); returns a
 * user-facing error string otherwise. Empty string → returns null (the
 * caller decides whether the empty state itself blocks submit).
 */
export function validateAddressFormat(coin, network, address) {
  const addr = (address || "").trim();
  if (!addr) return null;

  const evmCoins  = new Set(["ETH", "USDC", "USDT"]);
  const isEvmNet  = network === "ERC20" || network === "Ethereum";
  const isTronNet = network === "TRC20" || network === "Tron";

  if (isEvmNet && (coin === "ETH" || (evmCoins.has(coin) && network === "ERC20"))) {
    return RE_EVM.test(addr) ? null : `This doesn't look like a valid ${coin} address on ${network} (expected 0x + 40 hex).`;
  }
  if (isTronNet && (coin === "TRX" || coin === "USDT")) {
    return RE_TRON.test(addr) ? null : `This doesn't look like a valid ${coin} address on ${network} (expected T + 33 base58 chars).`;
  }
  if (coin === "BTC" && network === "Bitcoin") {
    return (RE_BTC_LEGACY.test(addr) || RE_BTC_BECH32.test(addr))
      ? null
      : `This doesn't look like a valid BTC address on Bitcoin (expected 1…, 3…, or bc1…).`;
  }
  if (coin === "LTC" && network === "Litecoin") {
    return (RE_LTC_LEGACY.test(addr) || RE_LTC_BECH32.test(addr))
      ? null
      : `This doesn't look like a valid LTC address on Litecoin (expected L…, M…, or ltc1…).`;
  }
  if (coin === "SOL" && network === "Solana") {
    return RE_SOL.test(addr) ? null : `This doesn't look like a valid SOL address on Solana (expected base58, 32–44 chars).`;
  }
  if (coin === "XRP" && network === "XRPL") {
    return RE_XRP.test(addr) ? null : `This doesn't look like a valid XRP address on XRPL (expected r…).`;
  }
  // Unknown combo — server will reject; block on the frontend too.
  return `Network ${network} is not supported for ${coin}.`;
}

/** True iff (coin, network, address) all pass the frontend guard. */
export function isWalletComplete(coin, network, address) {
  if (!coin || !network) return false;
  const allowed = COIN_NETWORKS[coin];
  if (!allowed || !allowed.includes(network)) return false;
  if (!(address || "").trim()) return false;
  return validateAddressFormat(coin, network, address) === null;
}
